/**
 * One Memory file's card (slice 1d spec §6.1): its title and when it loads,
 * the counter and bar, Write and Preview, who edited it last, and Save,
 * Discard and Undo. A save from a stale version shows the newer one, to keep
 * the draft over it or take it; a live change to a file with a draft asks
 * to reload. Members read, the owner edits.
 */
import { Button } from "@crew/ui/components/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@crew/ui/components/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { FieldError } from "@crew/ui/components/field";
import { Progress } from "@crew/ui/components/progress";
import { Spinner } from "@crew/ui/components/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@crew/ui/components/tabs";
import { Textarea } from "@crew/ui/components/textarea";
import { toneText } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { Ellipsis, Undo2 } from "lucide-react";
import { useState } from "react";
import { Streamdown } from "streamdown";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useNow } from "../../app/now";
import {
  refusalOf,
  useMemoryFile,
  useSaveFile,
  useUndoFile,
  type Budget,
  type MemoryFile,
  type SaveRefusal,
} from "./api";
import {
  actorName,
  budgetNote,
  canSave,
  changedNotice,
  conflicted,
  counter,
  discard,
  edit,
  editedBy,
  editorFor,
  entryHeading,
  everyRunLeft,
  isDirty,
  keepMine,
  overLimit,
  receive,
  reload,
  saved,
  takeTheirs,
  undone,
  type Editor,
  type Entry,
} from "./memory-view";

const BAR: Record<ReturnType<typeof counter>["tone"], string> = {
  muted: "[&_[data-slot=progress-indicator]]:bg-foreground/60",
  copper: "[&_[data-slot=progress-indicator]]:bg-copper",
  brick: "[&_[data-slot=progress-indicator]]:bg-brick",
};

/** The page keeps each file's editor (a draft outlives a switch); the card reads and updates it. */
type DraftProps = {
  draft: Editor | undefined;
  onDraft: (update: (draft: Editor | undefined) => Editor) => void;
};

export function FileEditor({
  entry,
  me,
  budget,
  ...drafts
}: { entry: Entry; me: Me; budget: Budget | undefined } & DraftProps) {
  const query = useMemoryFile(entry.meta?.id);
  if (!query.data) {
    return (
      <Card>
        <CardContent className="text-muted-foreground">
          {query.error ? errorHint(query.error) : <Spinner className="size-5" />}
        </CardContent>
      </Card>
    );
  }
  return <LoadedEditor file={query.data} entry={entry} me={me} budget={budget} {...drafts} />;
}

/**
 * The draft against the file (`memory-view.ts`'s `Editor`), and the saves and
 * undos that move it on. A refused save is a conflict to resolve, or a reason
 * shown under the editor. Every fresh copy of the file (a hub event's
 * refetch, a save's reply) meets the stored draft through `receive`.
 */
function useEditor(file: MemoryFile, { draft, onDraft }: DraftProps) {
  const view = (e: Editor | undefined) => receive(e ?? editorFor(file), file);
  const editor = view(draft);
  const setEditor = (update: (e: Editor) => Editor) => onDraft((e) => update(view(e)));
  const [refusal, setRefusal] = useState<SaveRefusal | null>(null);
  const save = useSaveFile(file.id);
  const undo = useUndoFile(file.id);

  const send = (from: Editor) => {
    const sent = from.draft;
    setRefusal(null);
    save.mutate(
      { content: sent, version: from.base.version },
      {
        onSuccess: (next) => setEditor((e) => saved(e, next, sent)),
        onError: (error) => {
          const refused = refusalOf(error) ?? { kind: "other", hint: errorHint(error) };
          if (refused.kind === "conflict") setEditor((e) => conflicted(e, refused.file));
          else setRefusal(refused);
        },
      },
    );
  };

  return {
    editor,
    refusal,
    busy: save.isPending || undo.isPending,
    saving: save.isPending,
    undoing: undo.isPending,
    // A conflict shows as the conflict panel, not as an error line.
    undoError: refusalOf(undo.error)?.kind === "conflict" ? null : undo.error,
    change: (text: string) => setEditor((e) => edit(e, text)),
    save: () => send(editor),
    discard: () => {
      setRefusal(null);
      setEditor(discard);
    },
    undo: () => {
      const from = editor.draft;
      // The version shown: a newer one since is a 409, shown like a save's conflict.
      undo.mutate(editor.base.version, {
        onSuccess: (f) => setEditor((e) => undone(e, f, from)),
        onError: (error) => {
          const refused = refusalOf(error);
          if (refused?.kind === "conflict") setEditor((e) => conflicted(e, refused.file));
        },
      });
    },
    reload: () => setEditor(reload),
    keepMine: () => {
      const next = keepMine(editor);
      setEditor(() => next);
      send(next);
    },
    takeTheirs: () => setEditor(takeTheirs),
  };
}

type EditorApi = ReturnType<typeof useEditor>;

function LoadedEditor({
  file,
  entry,
  me,
  budget,
  ...drafts
}: { file: MemoryFile; entry: Entry; me: Me; budget: Budget | undefined } & DraftProps) {
  const state = useEditor(file, drafts);
  const { title, about } = entryHeading(entry);
  const owner = me.user.role === "owner";
  const limit = budget?.file_max;
  return (
    <Card
      data-memory-file={file.name}
      data-version={state.editor.base.version}
      aria-label={title}
      className="gap-4"
    >
      <EditorHeader
        title={title}
        about={about}
        length={state.editor.draft.length}
        limit={limit}
        total={everyRunLeft(
          budget,
          entry.kind.everyRun,
          state.editor.draft.length,
          state.editor.base.content.length,
        )}
      />
      <CardContent className="flex flex-col gap-3">
        <Notices state={state} me={me} />
        <WriteAndPreview
          label={`${title} (${file.name})`}
          draft={state.editor.draft}
          // Read-only while an undo runs, so its reply can't land on new typing.
          readOnly={!owner || state.undoing}
          onChange={state.change}
          limit={limit}
        />
        <Problems state={state} limit={limit} />
      </CardContent>
      <EditorFooter state={state} me={me} owner={owner} limit={limit} />
    </Card>
  );
}

/** The title and what it is; the counter with its bar, and what's left of the every-run total. */
function EditorHeader({
  title,
  about,
  length,
  limit,
  total,
}: {
  title: string;
  about: string;
  length: number;
  limit: number | undefined;
  total: ReturnType<typeof everyRunLeft>;
}) {
  const count = counter(length, limit);
  return (
    <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between @2xl/memory:flex-col @2xl/memory:justify-start @4xl/memory:flex-row @4xl/memory:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <CardTitle className="font-display text-title font-normal">{title}</CardTitle>
        <p className="text-meta text-muted-foreground">{about}</p>
      </div>
      <div className="flex w-full shrink-0 flex-col gap-1.5 sm:w-48 @2xl/memory:w-full @4xl/memory:w-56">
        <span
          data-counter
          className={cn("self-end font-mono text-xs tabular-nums", toneText[count.tone])}
        >
          {count.text}
        </span>
        <Progress
          value={count.percent}
          aria-label={`${count.text} characters`}
          className={BAR[count.tone]}
        />
        {total ? (
          <span
            data-every-run
            className={cn("self-end text-2xs tabular-nums", toneText[total.tone])}
          >
            {total.text}
          </span>
        ) : null}
      </div>
    </CardHeader>
  );
}

/** A conflict to resolve, or a live change waiting behind "Reload?". */
function Notices({ state, me }: { state: EditorApi; me: Me }) {
  const { conflict, newer } = state.editor;
  if (conflict) {
    return (
      <ConflictPanel
        who={actorName(conflict.updated_by, me)}
        content={conflict.content}
        busy={state.busy}
        onKeepMine={state.keepMine}
        onUseTheirs={state.takeTheirs}
      />
    );
  }
  if (!newer) return null;
  return (
    <div
      role="status"
      data-memory-changed
      className="flex flex-wrap items-center gap-x-2 rounded-well bg-muted px-3 py-2 text-meta text-copy"
    >
      <span>{changedNotice(newer, me)}</span>{" "}
      <Button variant="link" size="xs" className="px-0" onClick={state.reload}>
        Reload?
      </Button>
    </div>
  );
}

/** The page's tall editor, or a compact one inside a list (a day's note, slice 1m). */
const HEIGHT = {
  page: "h-[60svh] md:h-[28rem] @2xl/memory:h-[max(18rem,calc(100dvh-27rem))] @4xl/memory:h-[max(20rem,calc(100dvh-24rem))]",
  compact: "h-56 md:h-64",
};

export function WriteAndPreview({
  label,
  draft,
  readOnly,
  onChange,
  limit,
  size = "page",
}: {
  label: string;
  draft: string;
  readOnly: boolean;
  onChange: (draft: string) => void;
  limit?: number;
  size?: keyof typeof HEIGHT;
}) {
  return (
    <Tabs defaultValue="write" className="gap-3">
      <TabsList variant="line">
        <TabsTrigger value="write">Write</TabsTrigger>
        <TabsTrigger value="preview">Preview</TabsTrigger>
      </TabsList>
      <TabsContent value="write">
        <Textarea
          aria-label={label}
          value={draft}
          readOnly={readOnly}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={overLimit(draft.length, limit) ? true : undefined}
          className={cn(
            "w-full resize-y [field-sizing:fixed] font-mono text-base leading-5 md:text-[13px]",
            HEIGHT[size],
          )}
        />
      </TabsContent>
      <TabsContent value="preview">
        <div
          data-memory-preview
          className={cn(
            "w-full overflow-y-auto rounded-lg border border-input px-4 py-3",
            HEIGHT[size],
          )}
        >
          {draft.trim() ? (
            <Streamdown className="space-y-3 text-body text-foreground" controls={false}>
              {draft}
            </Streamdown>
          ) : (
            <p className="text-body text-muted-foreground">Nothing here yet.</p>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}

/** Past the limit, a refused save, or a failed undo. */
function Problems({ state, limit }: { state: EditorApi; limit: number | undefined }) {
  const tooLong = overLimit(state.editor.draft.length, limit);
  const { refusal, undoError } = state;
  return (
    <>
      {tooLong ? <FieldError>{tooLong}</FieldError> : null}
      {refusal ? (
        <FieldError data-refusal={refusal.kind}>
          {refusal.kind === "over_budget" ? budgetNote(refusal.left) : refusal.hint}
        </FieldError>
      ) : null}
      {undoError ? <FieldError>{errorHint(undoError)}</FieldError> : null}
    </>
  );
}

/** Who edited it and when; the owner's Undo, Discard and Save. */
function EditorFooter({
  state,
  me,
  owner,
  limit,
}: {
  state: EditorApi;
  me: Me;
  owner: boolean;
  limit: number | undefined;
}) {
  const now = useNow();
  const { editor, busy } = state;
  const dirty = isDirty(editor);
  return (
    <CardFooter className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <span data-edited-by className="min-w-0 flex-1 text-meta text-muted-foreground">
        {editedBy(editor.base, me, now)}
      </span>
      {owner ? (
        <div className="flex items-center gap-2">
          <UndoMenu disabled={busy || dirty || editor.base.version <= 1} onUndo={state.undo} />
          <Button variant="ghost" disabled={!dirty || busy} onClick={state.discard}>
            Discard
          </Button>
          <Button disabled={!canSave(editor, limit) || busy} onClick={state.save}>
            {state.saving ? <Spinner data-icon="inline-start" /> : null}
            Save
          </Button>
        </div>
      ) : (
        <span className="text-meta text-muted-foreground">
          Only the owner can change these files.
        </span>
      )}
    </CardFooter>
  );
}

/**
 * A save ran into a newer version: show it, and keep the draft over it or
 * take it. `who` saved it, as a sentence names them ("the Chief of Staff").
 */
export function ConflictPanel({
  who,
  content,
  busy,
  onKeepMine,
  onUseTheirs,
}: {
  who: string;
  content: string;
  busy: boolean;
  onKeepMine: () => void;
  onUseTheirs: () => void;
}) {
  return (
    <div
      role="alert"
      data-memory-conflict
      className="flex flex-col gap-2.5 rounded-well border border-copper/40 bg-copper/5 p-3"
    >
      <p className="text-meta text-copy">
        {who.charAt(0).toUpperCase() + who.slice(1)} saved a newer version while you were editing.
        Keep yours on top of it, or use theirs.
      </p>
      <pre className="max-h-48 overflow-auto rounded-md bg-background px-3 py-2 font-mono text-xs leading-5 whitespace-pre-wrap text-foreground ring-1 ring-foreground/10">
        {content}
      </pre>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={onKeepMine}>
          Keep mine
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={onUseTheirs}>
          Use theirs
        </Button>
      </div>
    </div>
  );
}

function UndoMenu({ disabled, onUndo }: { disabled: boolean; onUndo: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button variant="ghost" size="icon" aria-label="More for this file" />}
      >
        <Ellipsis />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuItem disabled={disabled} onClick={onUndo}>
          <Undo2 />
          Undo the last change
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
